import { createMemo } from "solid-js";
import { buildUpcomingItems, groupReadingsByAsset, type UpcomingItem } from "@tendly/shared";
import {
  useAllUsageReadingsQuery,
  useReminderRulesQuery,
  useWarrantiesQuery,
} from "@tendly/shared/solid";
import { OFFLINE_CACHE_STALE_TIME_MS } from "@/lib/constants";
import { useTendlyDeps } from "@/features/assets/hooks/use-tendly-deps";

export interface UpcomingState {
  items: () => UpcomingItem[];
  /** Overdue + upcoming (sidebar badge). */
  dueCount: () => number;
  isLoading: () => boolean;
}

/**
 * User's upcoming due dates: dual rules (projected from the reading history) plus
 * warranties, sorted by urgency. Reactive: each new reading/service/warranty
 * recalculates immediately (local SQLite).
 */
export function useUpcoming(assetId?: () => string | undefined): UpcomingState {
  const deps = useTendlyDeps();
  const rules = useReminderRulesQuery({ ...deps, staleTime: OFFLINE_CACHE_STALE_TIME_MS });
  const warranties = useWarrantiesQuery({ ...deps, staleTime: OFFLINE_CACHE_STALE_TIME_MS });
  const readings = useAllUsageReadingsQuery({ ...deps, staleTime: OFFLINE_CACHE_STALE_TIME_MS });

  const items = createMemo(() => {
    const all = buildUpcomingItems({
      rules: rules.data ?? [],
      warranties: warranties.data ?? [],
      readingsByAsset: groupReadingsByAsset(readings.data ?? []),
    });
    const filterId = assetId?.();
    return filterId ? all.filter((item) => item.assetId === filterId) : all;
  });

  return {
    items,
    dueCount: () =>
      items().filter((item) => item.status === "overdue" || item.status === "due_soon").length,
    isLoading: () => rules.isLoading || warranties.isLoading || readings.isLoading,
  };
}
